import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Flex,
  HStack,
  VStack,
  Text,
  useToast,
  Spinner,
  Select,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Badge,
  Image,
  Progress,
  Icon,
  useKeyboardEvent,
  Grid,
  GridItem,
  Divider,
  Tag,
  TagLabel,
  SimpleGrid,
  Center,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react';
import {
  RefreshIcon,
  WarningIcon,
  SearchIcon
} from '../../utils/icons';
import { FaKeyboard, FaSearchPlus, FaSearchMinus, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { CameraSearchSelect } from '../common/SearchSelects';

// Componente para carregar imagem com autenticação
const AuthImage = ({ imagePath, loadImageUrl }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      if (!imagePath) {
        setImageSrc(null);
        setIsLoading(false);
        return;
      }

      // Se já está em cache, carregar rápido
      const cachedUrl = await loadImageUrl(imagePath);
      if (cachedUrl) {
        setImageSrc(cachedUrl);
        setHasError(false);
        setIsLoading(false);
      } else {
        setHasError(true);
        setIsLoading(false);
      }
    };

    fetchImage();
  }, [imagePath, loadImageUrl]);

  if (hasError) {
    return (
      <Box bg="gray.200" p={4} borderRadius="md" textAlign="center" h="300px" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={2}>
          <Text color="gray.600" fontSize="sm">Erro ao carregar imagem</Text>
          <Text color="gray.500" fontSize="xs">{imagePath?.split(/[\\/]/).pop()}</Text>
        </VStack>
      </Box>
    );
  }

  if (!imageSrc) {
    return (
      <Box h="300px" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  return (
    <Image
      src={imageSrc}
      alt="Infração"
      maxH="500px"
      maxW="100%"
      objectFit="contain"
      draggable="false"
      onError={() => setHasError(true)}
    />
  );
};

const ValidationTab = ({ userLevel }) => {
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [infractions, setInfractions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [validationUpdates, setValidationUpdates] = useState({}); // { id: status }
  const [isSaving, setIsSaving] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [cameraLocked, setCameraLocked] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [switchCameraDialogOpen, setSwitchCameraDialogOpen] = useState(false);
  const [camerasData, setCamerasData] = useState([]);
  const [suggestedCameraId, setSuggestedCameraId] = useState(null);
  const [imageUrls, setImageUrls] = useState({}); // Cache de URLs de imagens
  const [rejectionCount, setRejectionCount] = useState(0); // Contagem de rejeições para modal
  const [pendingCountDisplay, setPendingCountDisplay] = useState(0); // Contagem de pendentes para modal
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const toast = useToast();
  const abortControllerRef = useRef(null);
  const preloadAbortControllerRef = useRef(null);
  const cancelRef = useRef();
  const imageRef = useRef(null);
  const imageUrlsRef = useRef({}); // Ref para verificar cache sem causar re-render

  // Fetch cameras
  useEffect(() => {
    fetchCameras();
  }, []);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (sessionActive && selectedCameraId) {
        unlockCamera();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (preloadAbortControllerRef.current) {
        preloadAbortControllerRef.current.abort();
      }
      // Limpar cache de imagens ao desmontar
      setImageUrls({});
      imageUrlsRef.current = {};
    };
  }, [sessionActive, selectedCameraId]);

  const fetchCameras = async () => {
    setLoadingCameras(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/infractions/validation/cameras', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erro ao buscar câmeras');
      const data = await response.json();
      
      setCameras(data.cameras);
      setCamerasData(data.cameras);
      
      // Pre-selecionar a câmera sugerida (com mais infrações pendentes e disponível)
      if (data.suggested) {
        setSuggestedCameraId(data.suggested.id.toString());
        setSelectedCameraId(data.suggested.id.toString());
        setSelectedCamera(data.suggested);
        
        toast({
          title: 'Câmera sugerida',
          description: `${data.suggested.name} sugerida (${data.suggested.pending_count} infrações pendentes)`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Erro ao buscar câmeras:', error);
      toast({
        title: 'Erro ao carregar câmeras',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingCameras(false);
    }
  };

  const lockCamera = async (cameraId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/infractions/validation/lock-camera/${cameraId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Câmera já está sendo validada por outro usuário');
      }

      return true;
    } catch (error) {
      console.error('Erro ao fazer lock da câmera:', error);
      toast({
        title: 'Câmera indisponível',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  };

  const unlockCamera = async ({ finalize = false } = {}) => {
    if (!selectedCameraId) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/infractions/validation/unlock-camera/${selectedCameraId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ finalize })
      });
    } catch (error) {
      console.error('Erro ao liberar câmera:', error);
    }
  };

  const fetchPendingInfractions = async (cameraId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Tentar fazer lock da câmera
      const locked = await lockCamera(cameraId);
      if (!locked) return;

      const response = await fetch(`/api/infractions/validation/pending/${cameraId}?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erro ao buscar infrações pendentes');
      
      const data = await response.json();
      setInfractions(data.infractions || []);
      setCurrentIndex(0);
      setValidationUpdates({});
      setImageUrls({});
      imageUrlsRef.current = {};
      setSessionActive(true);
      setCameraLocked(true);

      if (data.infractions.length === 0) {
        toast({
          title: 'Nenhuma infração pendente',
          description: 'Não há infrações pendentes para esta câmera',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Infrações carregadas',
          description: `${data.infractions.length} infrações pendentes carregadas`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Erro ao buscar infrações pendentes:', error);
      toast({
        title: 'Erro ao carregar infrações',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setCameraLocked(false);
    } finally {
      setLoading(false);
    }
  };

  // Carregar imagem com autenticação (convertendo para base64 em vez de blob URL)
  const loadImageUrl = useCallback(async (imagePath, abortSignal = null) => {
    if (!imagePath) return null;
    
    const filename = imagePath.split(/[\\/]/).pop();
    
    // Verificar cache via ref (não causa re-render)
    if (imageUrlsRef.current[filename]) return imageUrlsRef.current[filename];
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/infractions/image/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: abortSignal
      });
      
      if (!response.ok) return null;
      
      const blob = await response.blob();
      
      // Converter blob para base64 em vez de usar blob URL
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Url = reader.result;
          // Atualizar ref imediatamente (sem re-render)
          imageUrlsRef.current[filename] = base64Url;
          // Atualizar state apenas para o componente de imagem atual
          setImageUrls(prev => ({
            ...prev,
            [filename]: base64Url
          }));
          resolve(base64Url);
        };
        reader.onerror = () => {
          console.error('Erro ao ler imagem');
          resolve(null);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error('Erro ao carregar imagem:', error);
      }
      return null;
    }
  }, []);

  // Função para resetar o zoom e a posição
  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Função para zoom com scroll do mouse (apenas zoom, sem drag)
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY < 0) {
      setZoom(prev => Math.min(prev + 0.1, 3));
    } else {
      setZoom(prev => Math.max(prev - 0.1, 1));
    }
  }, []);

  // Função para iniciar arrasto da imagem
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // Função para arrastar a imagem
  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoom > 1) {
      e.preventDefault();
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoom]);

  // Função para parar o arrasto
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Efeito para adicionar e remover event listeners globais de mouse
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Resetar o zoom quando muda de câmera ou infração
  useEffect(() => {
    resetZoom();
  }, [currentIndex, selectedCameraId, resetZoom]);

  const handleCameraChange = (e) => {
    const cameraId = e.target.value;
    const camera = cameras.find(cam => cam.id === parseInt(cameraId));
    
    setSelectedCameraId(cameraId);
    setSelectedCamera(camera || null);
    setInfractions([]);
    setCurrentIndex(0);
    setSessionActive(false);
    setCameraLocked(false);
    setValidationUpdates({});
    setImageUrls({});
    imageUrlsRef.current = {};
  };

  const handleStartValidation = async () => {
    if (!selectedCameraId) {
      toast({
        title: 'Selecione uma câmera',
        description: 'Escolha uma câmera antes de iniciar a validação',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (selectedCamera && selectedCamera.isAvailable === false) {
      toast({
        title: 'Câmera indisponível',
        description: 'Esta câmera está sendo validada por outro usuário',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await fetchPendingInfractions(selectedCameraId);
  };

  const updateInfractionStatus = (infractionId, status) => {
    setValidationUpdates(prev => ({
      ...prev,
      [infractionId]: status
    }));
  };

  const saveValidations = async () => {
    if (Object.keys(validationUpdates).length === 0) {
      toast({
        title: 'Nenhuma alteração',
        description: 'Nenhuma infração foi validada',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updates = Object.entries(validationUpdates).map(([id, status]) => ({
        id: parseInt(id),
        status
      }));

      const response = await fetch('/api/infractions/validation/batch-update', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error('Erro ao salvar validações');

      toast({
        title: 'Validações salvas',
        description: `${Object.keys(validationUpdates).length} infrações atualizadas`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      setValidationUpdates({});
    } catch (error) {
      console.error('Erro ao salvar validações:', error);
      toast({
        title: 'Erro ao salvar',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAfterFinish = async () => {
    setCompletionDialogOpen(false);

    // Liberar a câmera para outros usuários sem marcar as infrações não revisadas
    await unlockCamera({ finalize: false });

    setSessionActive(false);
    setCameraLocked(false);
    setInfractions([]);
    setCurrentIndex(0);
    setValidationUpdates({});
    setImageUrls({});
    imageUrlsRef.current = {};
    setRejectionCount(0);
    setPendingCountDisplay(0);
    setSelectedCameraId('');
    setSelectedCamera(null);
  };

  const handleFinishValidation = async () => {
    const rejectedCount = Object.values(validationUpdates).filter(s => s === 'rejected').length;
    const pendingCount = Object.values(validationUpdates).filter(s => s === 'pending').length;

    // Armazenar valores antes de salvar (que vai zerar validationUpdates)
    setRejectionCount(rejectedCount);
    setPendingCountDisplay(pendingCount);

    // Mostrar resumo antes de salvar
    toast({
      title: 'Resumo da validação',
      description: `${rejectedCount} infrações rejeitadas, ${pendingCount} marcadas como pendentes (revisadas)`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });

    await saveValidations();
    setCompletionDialogOpen(true);
  };

  const handleContinueValidation = async () => {
    setCompletionDialogOpen(false);
    await fetchPendingInfractions(selectedCameraId);
  };

  const handleSwitchCamera = () => {
    setCompletionDialogOpen(false);
    setSwitchCameraDialogOpen(true);
  };

  const confirmSwitchCamera = async () => {
    setSwitchCameraDialogOpen(false);
    await unlockCamera({ finalize: false });
    setSessionActive(false);
    setCameraLocked(false);
    setInfractions([]);
    setCurrentIndex(0);
    setValidationUpdates({});
    setImageUrls({});
    imageUrlsRef.current = {};
    setRejectionCount(0);
    setPendingCountDisplay(0);
    setSelectedCameraId('');
    setSelectedCamera(null);
  };

  const navigateToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const navigateToNext = () => {
    if (currentIndex < infractions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 1));
  };

  // Função para rejeitar e passar para próxima
  const handleRejectAndNext = () => {
    const currentInfraction = infractions[currentIndex];
    updateInfractionStatus(currentInfraction.id, 'rejected');
    navigateToNext();
  };

  // Função para marcar como pendente, contar como revisado e ir para próxima
  const handleMarkPending = () => {
    const currentInfraction = infractions[currentIndex];
    if (!currentInfraction) return;

    updateInfractionStatus(currentInfraction.id, 'pending');
    navigateToNext();
  };

  // Atalhos de teclado
  useEffect(() => {
    if (!sessionActive || infractions.length === 0) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        navigateToPrevious();
      } else if (e.key === 'ArrowRight') {
        navigateToNext();
      } else if (e.key.toLowerCase() === 'r') {
        handleRejectAndNext();
      } else if (e.key.toLowerCase() === 'p') {
        handleMarkPending();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, infractions, sessionActive, validationUpdates]);

  // Precarregar imagens em background
  useEffect(() => {
    if (!sessionActive || infractions.length === 0) return;

    // Cancelar precarregamento anterior
    if (preloadAbortControllerRef.current) {
      preloadAbortControllerRef.current.abort();
    }

    // Criar novo AbortController para este precarregamento
    preloadAbortControllerRef.current = new AbortController();
    const signal = preloadAbortControllerRef.current.signal;

    const preloadImages = async () => {
      if (signal.aborted) return;

      // Precarregar próxima imagem (mais importante)
      if (currentIndex + 1 < infractions.length) {
        const infraction = infractions[currentIndex + 1];
        if (infraction?.image_path) {
          const filename = infraction.image_path.split(/[\\/]/).pop();
          if (!imageUrlsRef.current[filename]) {
            await loadImageUrl(infraction.image_path, signal);
          }
        }
      }

      if (signal.aborted) return;

      // Precarregar segunda próxima
      if (currentIndex + 2 < infractions.length) {
        const infraction = infractions[currentIndex + 2];
        if (infraction?.image_path) {
          const filename = infraction.image_path.split(/[\\/]/).pop();
          if (!imageUrlsRef.current[filename]) {
            await loadImageUrl(infraction.image_path, signal);
          }
        }
      }
    };

    preloadImages();

    return () => {
      if (preloadAbortControllerRef.current) {
        preloadAbortControllerRef.current.abort();
      }
    };
  }, [currentIndex, infractions, sessionActive, loadImageUrl]);

  // Limpar cache de imagens antigas para economizar memória
  useEffect(() => {
    if (!sessionActive || infractions.length === 0) return;

    // Manter apenas imagens próximas ao índice atual
    const relevantFilenames = new Set();
    for (let i = Math.max(0, currentIndex - 2); i <= Math.min(infractions.length - 1, currentIndex + 3); i++) {
      const infraction = infractions[i];
      if (infraction?.image_path) {
        relevantFilenames.add(infraction.image_path.split(/[\\/]/).pop());
      }
    }

    // Limpar do ref e state as entradas distantes
    const cacheKeys = Object.keys(imageUrlsRef.current);
    if (cacheKeys.length > 8) {
      const keysToRemove = cacheKeys.filter(key => !relevantFilenames.has(key));
      if (keysToRemove.length > 0) {
        keysToRemove.forEach(key => {
          delete imageUrlsRef.current[key];
        });
        setImageUrls(prev => {
          const newCache = { ...prev };
          keysToRemove.forEach(key => {
            delete newCache[key];
          });
          return newCache;
        });
      }
    }
  }, [currentIndex, sessionActive, infractions]);

  if (userLevel < 2) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4} py={8}>
            <WarningIcon boxSize={12} color="gray.400" />
            <Text fontSize="lg" color="gray.500" textAlign="center">
              Você não tem permissão para acessar esta funcionalidade
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  const currentInfraction = infractions[currentIndex];
  const currentStatus = currentInfraction ? validationUpdates[currentInfraction.id] : null;
  const validatedCount = Object.keys(validationUpdates).length;
  const progressPercentage = infractions.length > 0 ? (validatedCount / infractions.length) * 100 : 0;

  return (
    <Box>
      <Heading size="lg" display="flex" alignItems="center" mb={6}>
        <Icon as={SearchIcon} mr={3} color="blue.500" />
        Validação de Infrações
      </Heading>

      {!sessionActive ? (
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel fontWeight="medium">Selecionar Câmera</FormLabel>
                <HStack spacing={4}>
                  <Box flex="1">
                    <CameraSearchSelect
                      cameras={cameras}
                      value={selectedCameraId}
                      onChange={handleCameraChange}
                      placeholder="Buscar câmera por nome ou ID..."
                      size="lg"
                      isDisabled={loadingCameras}
                    />
                  </Box>
                  <Button
                    colorScheme="green"
                    onClick={handleStartValidation}
                    isLoading={loading}
                    loadingText="Carregando..."
                    isDisabled={!selectedCameraId || selectedCamera?.isAvailable === false}
                    minW="180px"
                  >
                    Iniciar Validação
                  </Button>
                </HStack>
              </FormControl>

              {selectedCamera && camerasData.length > 0 && (
                <Box bg="blue.50" p={4} borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                  <VStack align="start" spacing={2}>
                    <Flex gap={4} align="center" fontSize="sm" w="full">
                      <Box>
                        <Text fontWeight="medium" color="gray.700">
                          {selectedCamera.name}
                        </Text>
                        <Text color="gray.600" fontSize="xs">
                          {selectedCamera.location}
                        </Text>
                      </Box>
                      <Box ml="auto" textAlign="right">
                        <Badge 
                          colorScheme={selectedCamera.isAvailable ? 'green' : 'red'}
                          fontSize="sm"
                          mb={1}
                        >
                          {selectedCamera.isAvailable ? 'Disponível' : 'Em validação'}
                        </Badge>
                        <Text fontWeight="bold" color="blue.600">
                          {selectedCamera.pending_count} infrações pendentes
                        </Text>
                      </Box>
                    </Flex>
                    {suggestedCameraId === selectedCameraId && (
                      <Text fontSize="xs" color="green.600" fontWeight="medium">
                        ✓ Esta é a câmera com mais infrações pendentes disponíveis
                      </Text>
                    )}
                  </VStack>
                </Box>
              )}

              <Text fontSize="sm" color="gray.600">
                Selecione uma câmera e clique em "Iniciar Validação" para começar o processo de validação das últimas 100 infrações pendentes.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      ) : infractions.length === 0 ? (
        <Card>
          <CardBody>
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color="blue.500" />
              <Text>Carregando infrações...</Text>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* Header com informações */}
          <Card>
            <CardBody>
              <Flex justify="space-between" align="center" mb={4}>
                <Flex align="center" gap={4}>
                  <Badge colorScheme="blue" padding="8px 12px">
                    {selectedCamera?.name}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    Infração {currentIndex + 1} de {infractions.length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Validadas: {validatedCount}
                  </Text>
                </Flex>
                <HStack>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => handleZoomOut()}
                    leftIcon={<FaSearchMinus />}
                  >
                    -
                  </Button>
                  <Text fontSize="sm" minW="60px" textAlign="center">
                    {Math.round(zoom * 100)}%
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => handleZoomIn()}
                    leftIcon={<FaSearchPlus />}
                  >
                    +
                  </Button>
                </HStack>
              </Flex>

              <Progress
                value={progressPercentage}
                size="md"
                colorScheme="green"
                borderRadius="md"
              />
            </CardBody>
          </Card>

          {/* Imagem e controles */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="center">
                {currentInfraction?.image_path ? (
                  <Box
                    position="relative"
                    maxH="500px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="md"
                    overflow="hidden"
                    bg="gray.100"
                    w="full"
                    onMouseDown={handleMouseDown}
                    onWheel={handleWheel}
                    cursor={zoom > 1 ? "grab" : "default"}
                    style={{ touchAction: "none", userSelect: "none" }}
                  >
                    <Box
                      ref={imageRef}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      style={{
                        transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                        transformOrigin: "center",
                        transition: isDragging ? "none" : "transform 0.2s ease-out"
                      }}
                    >
                      <AuthImage
                        imagePath={currentInfraction.image_path}
                        loadImageUrl={loadImageUrl}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Center h="300px" w="full" bg="gray.100" borderRadius="md">
                    <Text color="gray.500">Sem imagem disponível</Text>
                  </Center>
                )}

                {/* Informações da infração */}
                <SimpleGrid columns={2} spacing={4} w="full">
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">Câmera</Text>
                    <Text fontSize="sm">{selectedCamera?.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">Data</Text>
                    <Text fontSize="sm">
                      {new Date(currentInfraction?.detected_at).toLocaleDateString('pt-BR')}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">Placa</Text>
                    <Text fontSize="sm">{currentInfraction?.plate || 'Não detectada'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">Confiabilidade</Text>
                    <Text fontSize="sm">{(currentInfraction?.confidence * 100).toFixed(1)}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">Tipo de Veículo</Text>
                    <Text fontSize="sm">{currentInfraction?.vehicle_type || 'Desconhecido'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">Status Atual</Text>
                    <Badge
                      colorScheme={
                        currentStatus === 'confirmed'
                          ? 'green'
                          : currentStatus === 'rejected'
                          ? 'red'
                          : 'gray'
                      }
                    >
                      {currentStatus || 'Pendente'}
                    </Badge>
                  </Box>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          {/* Atalhos de teclado */}
          <Card bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.400">
            <CardBody>
              <HStack spacing={6} fontSize="sm">
                <Flex align="center" gap={2}>
                  <Icon as={FaKeyboard} />
                  <Text fontWeight="medium">Atalhos:</Text>
                </Flex>
                <Text>← → : Navegação</Text>
                <Text><Badge colorScheme="red">R</Badge> : Rejeitar e próxima</Text>
                <Text><Badge colorScheme="orange">P</Badge> : Marcar como Pendente</Text>
                <Text><Badge colorScheme="purple">+/-</Badge> : Zoom</Text>
              </HStack>
            </CardBody>
          </Card>

          {/* Botões de ação */}
          <Flex justify="space-between" gap={4}>
            <HStack>
              <Button
                onClick={navigateToPrevious}
                isDisabled={currentIndex === 0}
                colorScheme="blue"
                variant="outline"
              >
                ← Anterior
              </Button>
              <Button
                onClick={navigateToNext}
                isDisabled={currentIndex === infractions.length - 1}
                colorScheme="blue"
                variant="outline"
              >
                Próxima →
              </Button>
            </HStack>

            <HStack>
              <Button
                onClick={handleRejectAndNext}
                colorScheme="red"
                leftIcon={<FaTimesCircle />}
                size="lg"
              >
                Rejeitar (R)
              </Button>
              <Button
                onClick={handleMarkPending}
                colorScheme="orange"
                leftIcon={<RefreshIcon />}
                size="lg"
              >
                Pendente (P)
              </Button>
            </HStack>

            <HStack>
              <Button
                onClick={handleFinishValidation}
                colorScheme="teal"
                size="lg"
                isLoading={isSaving}
                loadingText="Salvando..."
              >
                Finalizar ({validatedCount}/{infractions.length})
              </Button>
            </HStack>
          </Flex>
        </VStack>
      )}

      {/* Diálogo de conclusão */}
      <AlertDialog isOpen={completionDialogOpen} leastDestructiveRef={cancelRef} size="xl" isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent py={8}>
            <AlertDialogHeader fontSize="2xl" fontWeight="bold">
              ✓ Validação Concluída com Sucesso!
            </AlertDialogHeader>
            <AlertDialogBody fontSize="lg" py={6}>
              <VStack spacing={4} align="start">
                <Box>
                  <Text fontWeight="bold" color="red.600" fontSize="lg">
                    {rejectionCount} infrações rejeitadas
                  </Text>
                  <Text fontWeight="bold" color="orange.600" fontSize="lg">
                    {pendingCountDisplay} infrações marcadas como pendentes
                  </Text>
                </Box>

                <Divider />
                <Text fontSize="md" color="gray.600" mt={2}>
                  Deseja continuar validando infrações desta câmera ou trocar de câmera?
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={handleCloseAfterFinish}
                colorScheme="blue"
                variant="outline"
                size="lg"
              >
                Fechar
              </Button>
              <Button
                onClick={handleSwitchCamera}
                colorScheme="orange"
                ml={3}
                size="lg"
              >
                Trocar Câmera
              </Button>
              <Button
                onClick={handleContinueValidation}
                colorScheme="green"
                ml={3}
                isLoading={loading}
                size="lg"
              >
                Continuar com esta Câmera
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Diálogo de troca de câmera */}
      <AlertDialog isOpen={switchCameraDialogOpen} leastDestructiveRef={cancelRef} size="xl" isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Liberar Câmera
            </AlertDialogHeader>
            <AlertDialogBody>
              A câmera {selectedCamera?.name} será liberada para outros usuários. Tem certeza?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setSwitchCameraDialogOpen(false)}
                colorScheme="blue"
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmSwitchCamera}
                colorScheme="red"
                ml={3}
              >
                Liberar e Trocar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ValidationTab;
